/**
 * Production-rate data for a single Item, broken down by facility and quantity.
 * Returned by the server endpoint `order/productionrates/item` and cached client-side.
 */

export interface QuantityBreak {
  /** Inclusive lower bound of this break (1-based). */
  minQty: number;
  /** Inclusive upper bound; use Number.MAX_SAFE_INTEGER for open-ended top tier. */
  maxQty: number;
  /** Per-book unit cost in dollars within this break. */
  unitCost: number;
  /** Flat dollar cost added once per order regardless of quantity. */
  perOrderCost: number;
  /**
   * Opaque server-issued signature covering this row's inputs + outputs.
   * Echoed in the place-order envelope so the server can skip the
   * production-rate recompute on submit. See
   * `docs/contracts/outbox/2026-06-24-production-rate-signatures.md`.
   *
   * Optional: server may emit this per row (Shape A) or
   * `ProductionRateTable.tableSignature` once (Shape B). The client
   * accepts whichever the server sends.
   */
  signature?: string;
}

export interface FacilityRate {
  facilityId: number;
  facilityName?: string;
  /** True when this facility holds stock that can satisfy orders without reprinting. */
  canInventory: boolean;
  /** True when this facility can print this item to order. */
  canPrint: boolean;
  /** Books currently on hand at this facility (uncommitted). */
  inventoryAvailable: number;
  /** Quantity-tier pricing for the print-to-order path (and inventory if priced). */
  breaks: QuantityBreak[];
}

export class ProductionRateTable {
  itemId: number;
  facilities: FacilityRate[];
  fetchedAt: Date;
  /**
   * Optional table-level signature (Shape B). When present, covers the
   * whole `(itemId, facilities[])` payload. Per-row signatures on
   * `QuantityBreak.signature` (Shape A) are preferred — see the outbox
   * message — but the model accepts both shapes.
   */
  tableSignature?: string;

  constructor(obj?: any) {
    this.itemId = 0;
    this.facilities = [];
    this.fetchedAt = new Date();
    if (obj) {
      this.itemId = obj.itemId || 0;
      if (typeof obj.tableSignature === 'string') {
        this.tableSignature = obj.tableSignature;
      }
      const facilities = obj.facilities || [];
      this.facilities = facilities.map((f: any) => ({
        facilityId: Number(f.facilityId) || 0,
        facilityName: f.facilityName,
        canInventory: !!f.canInventory,
        canPrint: !!f.canPrint,
        inventoryAvailable: Number(f.inventoryAvailable) || 0,
        breaks: (f.breaks || []).map((b: any) => ({
          minQty: Number(b.minQty) || 0,
          maxQty: Number(b.maxQty) || Number.MAX_SAFE_INTEGER,
          unitCost: Number(b.unitCost) || 0,
          perOrderCost: Number(b.perOrderCost) || 0,
          signature: typeof b.signature === 'string' ? b.signature : undefined
        }))
      }));
    }
  }

  /** Returns unitCost + perOrderCost for the given facility & quantity, or null if not available. */
  getCostForFacility(facilityId: number, quantity: number): { unitCost: number; perOrderCost: number } | null {
    const f = this.facilities.find(x => x.facilityId === facilityId);
    if (!f) { return null; }
    const b = f.breaks.find(x => quantity >= x.minQty && quantity <= x.maxQty);
    if (!b) { return null; }
    return { unitCost: b.unitCost, perOrderCost: b.perOrderCost };
  }

  /**
   * Returns the per-row signature for the given facility & quantity (Shape A),
   * or the table-level signature as a fallback (Shape B), or undefined if the
   * server hasn't emitted signatures yet.
   */
  getSignatureForFacility(facilityId: number, quantity: number): string | undefined {
    const f = this.facilities.find(x => x.facilityId === facilityId);
    if (f) {
      const b = f.breaks.find(x => quantity >= x.minQty && quantity <= x.maxQty);
      if (b && b.signature) { return b.signature; }
    }
    return this.tableSignature;
  }

  /** Returns the cheapest facility (by total cost = unitCost*qty + perOrderCost) that can satisfy `quantity`. */
  getCheapestFacility(quantity: number): { facilityId: number; totalCost: number } | null {
    let best: { facilityId: number; totalCost: number } | null = null;
    for (const f of this.facilities) {
      // Must be able to satisfy via inventory or print
      const canSatisfy = (f.canInventory && f.inventoryAvailable >= quantity) || f.canPrint;
      if (!canSatisfy) { continue; }
      const b = f.breaks.find(x => quantity >= x.minQty && quantity <= x.maxQty);
      if (!b) { continue; }
      const total = b.unitCost * quantity + b.perOrderCost;
      if (!best || total < best.totalCost) {
        best = { facilityId: f.facilityId, totalCost: total };
      }
    }
    return best;
  }

  /**
   * Returns the per-facility total cost (`unitCost * quantity + perOrderCost`)
   * for every facility that can satisfy the quantity, sorted by cost ascending.
   * Used by the Stage-1 UI hint when more than one facility has a price.
   */
  getFacilityCosts(quantity: number): Array<{ facilityId: number; facilityName?: string; totalCost: number }> {
    const out: Array<{ facilityId: number; facilityName?: string; totalCost: number }> = [];
    for (const f of this.facilities) {
      const canSatisfy = (f.canInventory && f.inventoryAvailable >= quantity) || f.canPrint;
      if (!canSatisfy) { continue; }
      const b = f.breaks.find(x => quantity >= x.minQty && quantity <= x.maxQty);
      if (!b) { continue; }
      out.push({
        facilityId: f.facilityId,
        facilityName: f.facilityName,
        totalCost: b.unitCost * quantity + b.perOrderCost
      });
    }
    out.sort((a, b) => a.totalCost - b.totalCost);
    return out;
  }

  /**
   * Detailed per-facility breakdown for the given quantity. Includes the
   * per-unit cost, the per-order flat, and the line total. Used by the
   * Stage-1 cart-page price block when more than one facility prices the
   * item differently — the user sees "$3.20/ea × 5 + $1.00 = $17.00 at
   * Phoenix, $3.45/ea × 5 + $1.00 = $18.25 at Houston" instead of just
   * the cheapest line. Sorted by line total ascending.
   */
  getFacilityCostsDetailed(quantity: number): Array<{
    facilityId: number;
    facilityName?: string;
    unitCost: number;
    perOrderCost: number;
    totalCost: number;
  }> {
    const out: Array<{
      facilityId: number;
      facilityName?: string;
      unitCost: number;
      perOrderCost: number;
      totalCost: number;
    }> = [];
    for (const f of this.facilities) {
      const canSatisfy = (f.canInventory && f.inventoryAvailable >= quantity) || f.canPrint;
      if (!canSatisfy) { continue; }
      const b = f.breaks.find(x => quantity >= x.minQty && quantity <= x.maxQty);
      if (!b) { continue; }
      out.push({
        facilityId: f.facilityId,
        facilityName: f.facilityName,
        unitCost: b.unitCost,
        perOrderCost: b.perOrderCost,
        totalCost: b.unitCost * quantity + b.perOrderCost
      });
    }
    out.sort((a, b) => a.totalCost - b.totalCost);
    return out;
  }

  /**
   * Returns the quantity-break ladder for the given facility, each row
   * tagged with the `isCurrent` flag identifying which break the
   * currentQuantity falls into. Used by the Stage-1 cart-page "buy
   * more to save" hint that shows the unit cost at each break tier.
   *
   * Consecutive tiers that carry the same `unitCost` AND same
   * `perOrderCost` are COLLAPSED into a single displayed row spanning
   * `firstTier.minQty` through `lastMergedTier.maxQty` (the open-top
   * sentinel `Number.MAX_SAFE_INTEGER` on the terminal tier is
   * preserved). The rate-config engine authors adjacent equal-price
   * tiers legitimately (e.g. `1-4 @ $10`, `5-9 @ $10`, `10-19 @ $9.50`
   * — the first two shouldn't display as two rows because there's no
   * pricing difference for the user to react to); collapsing here
   * keeps the "buy more to save" hint honest and short. The
   * `isCurrent` flag on the merged row is true if the current
   * quantity falls anywhere in the merged span.
   *
   * Costs are compared with a half-cent tolerance to guard against
   * floating-point noise across rate-config recompilations.
   *
   * Returns an empty array when the facility doesn't exist, has no
   * breaks, or (after collapsing) has only a single row — either
   * because the facility was authored with a single tier, or because
   * every tier carried the same unit + per-order cost (no
   * quantity-based variation to surface). The Stage-1 cart-page
   * template also suppresses the block on `.length < 2`, so the
   * "1-19 / 20-99 / 100-499 / 500+ all $12.77" degenerate case
   * disappears entirely.
   */
  getQuantityBreaks(facilityId: number, currentQuantity: number): Array<{
    minQty: number;
    maxQty: number;
    unitCost: number;
    perOrderCost: number;
    isCurrent: boolean;
  }> {
    const f = this.facilities.find(x => x.facilityId === facilityId);
    if (!f || !f.breaks || f.breaks.length < 2) { return []; }
    const sorted = f.breaks
      .slice() // don't mutate the cached table
      .sort((a, b) => a.minQty - b.minQty);
    const EPS = 0.005; // half a cent
    const sameRate = (x: { unitCost: number; perOrderCost: number },
                      y: { unitCost: number; perOrderCost: number }): boolean =>
      Math.abs(x.unitCost - y.unitCost) < EPS
      && Math.abs(x.perOrderCost - y.perOrderCost) < EPS;
    const collapsed: Array<{
      minQty: number; maxQty: number; unitCost: number; perOrderCost: number;
    }> = [];
    for (const br of sorted) {
      const last = collapsed[collapsed.length - 1];
      // Merge into the previous row when the rate matches. We do NOT
      // require the tiers to be strictly contiguous
      // (`last.maxQty + 1 === br.minQty`) — the rate-config engine
      // defines the tier ladder densely, and any gap between rows
      // would resolve as "no matching break" at evaluation time
      // anyway. Merging on matching rate alone keeps the display
      // collapsed even when the underlying tier ladder has cosmetic
      // seams.
      if (last && sameRate(last, br)) {
        last.maxQty = br.maxQty;
      } else {
        collapsed.push({
          minQty: br.minQty,
          maxQty: br.maxQty,
          unitCost: br.unitCost,
          perOrderCost: br.perOrderCost
        });
      }
    }
    // After collapsing, if only a single row remains there's nothing
    // for the user to see — the display block hides itself on
    // `.length < 2` (both the html `*ngIf` and the caller's own
    // length check), and returning `[]` here keeps that contract
    // explicit at the source.
    if (collapsed.length < 2) { return []; }
    return collapsed.map(b => ({
      minQty: b.minQty,
      maxQty: b.maxQty,
      unitCost: b.unitCost,
      perOrderCost: b.perOrderCost,
      isCurrent: currentQuantity >= b.minQty && currentQuantity <= b.maxQty
    }));
  }

  /** All facility ids that can produce or ship this item at the given quantity. */
  getProducibleFacilityIds(quantity: number): number[] {
    return this.facilities
      .filter(f => f.canPrint || (f.canInventory && f.inventoryAvailable >= quantity))
      .map(f => f.facilityId);
  }

  /** Facility ids that have enough inventory on hand for the given quantity. */
  getInventoryFacilityIds(quantity: number): number[] {
    return this.facilities
      .filter(f => f.canInventory && f.inventoryAvailable >= quantity)
      .map(f => f.facilityId);
  }

  /**
   * True when SOME facility has AT LEAST one authored `break`. Used by
   * the cart to distinguish "empty rate table — nothing priced at any
   * quantity, treat as ERROR" from "rate table populated but nothing
   * covers THIS specific quantity — treat as INFEASIBLE and surface
   * the working ranges to the user".
   */
  hasAnyFeasibleQuantity(): boolean {
    for (const f of (this.facilities || [])) {
      if (f && f.breaks && f.breaks.length > 0) { return true; }
    }
    return false;
  }

  /**
   * Union of feasible quantity ranges across every facility, collapsed so
   * consecutive spans (or spans from different facilities that abut or
   * overlap) merge into one displayed row. Used by the cart-page when a
   * user has entered a quantity that no facility can price — the UI
   * says "no rate for this quantity; try 1-4 or 10+".
   *
   * Returned entries carry the CHEAPEST `unitCost` + `perOrderCost`
   * observed within the range across all facilities that cover it — so
   * the user sees the same "best-case" price they'd get at that
   * quantity. This mirrors the cheapest-facility choice
   * {@link getCheapestFacility} makes at evaluation time.
   *
   * The terminal open-top tier's `maxQty` stays at
   * `Number.MAX_SAFE_INTEGER` so the UI can format it as `<min>+`.
   */
  getAllFeasibleRanges(): Array<{
    minQty: number;
    maxQty: number;
    unitCost: number;
    perOrderCost: number;
  }> {
    // Collect every (minQty, maxQty, unitCost, perOrderCost) span.
    const spans: Array<{
      minQty: number; maxQty: number; unitCost: number; perOrderCost: number;
    }> = [];
    for (const f of (this.facilities || [])) {
      for (const b of (f.breaks || [])) {
        spans.push({
          minQty: b.minQty,
          maxQty: b.maxQty,
          unitCost: b.unitCost,
          perOrderCost: b.perOrderCost
        });
      }
    }
    if (spans.length === 0) { return []; }
    // Sort by minQty ascending. Ties: lower maxQty first so we take
    // the narrower span as the starting seed and only extend outward.
    spans.sort((a, b) => (a.minQty - b.minQty) || (a.maxQty - b.maxQty));
    // Walk the sorted list, merging any span that overlaps or abuts
    // the running "current" span. When merging, keep the cheapest
    // unit + per-order rate observed so the user sees the best-case
    // price for that quantity (matches the cheapest-facility choice
    // the evaluator makes).
    const merged: Array<{
      minQty: number; maxQty: number; unitCost: number; perOrderCost: number;
    }> = [];
    for (const s of spans) {
      const last = merged[merged.length - 1];
      // Consecutive tiers "abut" when `last.maxQty + 1 === s.minQty`.
      // Overlaps when `last.maxQty >= s.minQty`. Treat the open-top
      // sentinel as an overlap so the terminal tier absorbs anything
      // strictly beyond it (defensive; the sort already puts open-top
      // last but a facility with a low open-top tier could still show
      // up early).
      const abuts = last && (last.maxQty + 1 >= s.minQty
                             || last.maxQty >= Number.MAX_SAFE_INTEGER);
      if (last && abuts) {
        last.maxQty = Math.max(last.maxQty, s.maxQty);
        if (s.unitCost < last.unitCost) { last.unitCost = s.unitCost; }
        if (s.perOrderCost < last.perOrderCost) { last.perOrderCost = s.perOrderCost; }
      } else {
        merged.push({...s});
      }
    }
    return merged;
  }
}

